import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Image } from '../../types/entities/image.entity';
import { Album } from '../../types/entities/album.entity';
import { getImageLabel, isGalleryVideo } from '../../utils/galleryMedia';

interface GalleryLightboxProps {
    open: boolean;
    images: Image[];
    currentIndex: number;
    onClose: () => void;
    onChangeIndex: (index: number) => void;
    onDelete: (image: Image) => void;
    onDisplayNameSave?: (imageId: string, displayName: string) => Promise<void>;
    onRemoveFromAlbum?: (image: Image) => void;
    albums?: Album[];
    currentAlbumId?: string;
}

export const GalleryLightbox = ({
    open,
    images,
    currentIndex,
    onClose,
    onChangeIndex,
    onDelete,
    onDisplayNameSave,
    onRemoveFromAlbum,
    albums,
    currentAlbumId,
}: GalleryLightboxProps) => {
    const { t } = useTranslation();
    const image = images[currentIndex];
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;
    const [displayName, setDisplayName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const imageAlbums = useMemo(() => {
        if (!albums?.length || !image?.album_ids?.length) {
            return [];
        }

        const byId = new Map(albums.map((album) => [album.id, album]));
        return image.album_ids
            .map((id) => byId.get(id))
            .filter((album): album is Album => album !== undefined);
    }, [albums, image?.album_ids]);

    const displayedAlbums = useMemo(() => {
        if (!currentAlbumId) {
            return imageAlbums;
        }

        return imageAlbums.filter((album) => album.id !== currentAlbumId);
    }, [currentAlbumId, imageAlbums]);

    useEffect(() => {
        if (image) {
            setDisplayName(getImageLabel(image));
        }
    }, [image?.id, image?.display_name, image?.original_name]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            } else if (event.key === 'ArrowLeft' && hasPrev) {
                onChangeIndex(currentIndex - 1);
            } else if (event.key === 'ArrowRight' && hasNext) {
                onChangeIndex(currentIndex + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, currentIndex, hasPrev, hasNext, onClose, onChangeIndex]);

    if (!image) {
        return null;
    }

    const handleSaveDisplayName = async () => {
        if (!onDisplayNameSave || displayName.trim() === (image.display_name ?? '').trim()) {
            return;
        }

        setIsSaving(true);
        try {
            await onDisplayNameSave(image.id, displayName.trim());
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    bgcolor: 'rgba(0,0,0,0.95)',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 16, right: 16, color: 'common.white' }}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>

                {onRemoveFromAlbum && (
                    <IconButton
                        onClick={() => onRemoveFromAlbum(image)}
                        sx={{ position: 'absolute', top: 16, right: 128, color: 'warning.light' }}
                        aria-label="remove from album"
                    >
                        <LinkOffIcon />
                    </IconButton>
                )}

                <IconButton
                    onClick={() => onDelete(image)}
                    sx={{ position: 'absolute', top: 16, right: 72, color: 'error.light' }}
                    aria-label="delete"
                >
                    <DeleteIcon />
                </IconButton>

                {hasPrev && (
                    <IconButton
                        onClick={() => onChangeIndex(currentIndex - 1)}
                        sx={{
                            position: 'absolute',
                            left: 16,
                            color: 'common.white',
                            bgcolor: 'rgba(255,255,255,0.12)',
                        }}
                        aria-label="previous"
                    >
                        <ChevronLeftIcon fontSize="large" />
                    </IconButton>
                )}

                {hasNext && (
                    <IconButton
                        onClick={() => onChangeIndex(currentIndex + 1)}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            color: 'common.white',
                            bgcolor: 'rgba(255,255,255,0.12)',
                        }}
                        aria-label="next"
                    >
                        <ChevronRightIcon fontSize="large" />
                    </IconButton>
                )}

                <Box sx={{ maxWidth: '100%', maxHeight: 'calc(100vh - 160px)', textAlign: 'center' }}>
                    {isGalleryVideo(image) ? (
                        <video
                            key={image.id}
                            controls
                            autoPlay
                            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 180px)' }}
                            src={image.path}
                            poster={image.preview ?? undefined}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={image.path}
                            alt={getImageLabel(image)}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 'calc(100vh - 180px)',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </Box>

                <Stack
                    spacing={1}
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 'min(520px, calc(100% - 32px))',
                        alignItems: 'center',
                    }}
                >
                    {onDisplayNameSave && (
                        <Stack direction="row" spacing={1} width="100%">
                            <TextField
                                size="small"
                                fullWidth
                                label={t('gallery.displayName')}
                                value={displayName}
                                onChange={(event) => setDisplayName(event.target.value)}
                                sx={{
                                    '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.92)' },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSaveDisplayName}
                                disabled={isSaving || !displayName.trim()}
                            >
                                {t('common.save')}
                            </Button>
                        </Stack>
                    )}
                    {image.original_name && (
                        <Typography variant="caption" color="grey.500">
                            {t('gallery.originalName')}: {image.original_name}
                        </Typography>
                    )}
                    {albums && (
                        <Box textAlign="center" width="100%">
                            <Typography variant="caption" color="grey.500" display="block" mb={0.5}>
                                {currentAlbumId ? t('gallery.alsoInAlbums') : t('gallery.inAlbums')}
                            </Typography>
                            {displayedAlbums.length === 0 ? (
                                <Typography variant="caption" color="grey.400">
                                    {currentAlbumId
                                        ? t('gallery.notInAnyOtherAlbum')
                                        : t('gallery.notInAnyAlbum')}
                                </Typography>
                            ) : (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="wrap"
                                    useFlexGap
                                    justifyContent="center"
                                >
                                    {displayedAlbums.map((album) => (
                                        <Chip
                                            key={album.id}
                                            component={RouterLink}
                                            to={`/gallery/album/${album.id}`}
                                            label={album.name}
                                            size="small"
                                            clickable
                                            onClick={onClose}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.92)',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.98)' },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}
                    <Typography variant="caption" color="grey.400">
                        {currentIndex + 1} / {images.length}
                    </Typography>
                </Stack>
            </Box>
        </Dialog>
    );
};
